// ============================================================
// theme.ts
// The app theme — single source of truth for reading, resolving, applying
// and caching the user's choice:
//   Light · Dark · Match my device (prefers-color-scheme, live) ·
//   Automatic (dark from UK sunset to sunrise — or fixed times).
//
// The choice is SAVED TO THE ACCOUNT (User.appearance, PATCH /api/auth/me)
// so it follows the user across devices; this device keeps a local cache
// for first paint:
//   casevo_theme       the mode ('light' | 'dark' | 'device' | 'auto';
//                      the old values 'light' / 'dark' carry straight over)
//   casevo_theme_auto  { fixed, from, to } for Automatic
//   casevo_theme_dark  Automatic's dark periods for the next 60 days,
//                      [[startMs, endMs], …] — worked out here, so the
//                      boot script (public/theme-boot.js, loaded from
//                      public/index.html) can apply the right class BEFORE
//                      first paint without the sun maths. Keep the keys and
//                      the rules in sync with that script.
//
// `.dark` on <html> is the app theme; the Calendar's own focus switch wraps
// its shell in `.dark` / `.casevo-light` on top (index.css), untouched here.
// ============================================================
import { create } from 'zustand'
import { sunTimes, ukInstant, ukParts } from './sunTimes'

import type { Appearance, Theme, ThemeMode } from '../types/appearance.types'
export type { Appearance, Theme, ThemeMode }
export const DEFAULT_APPEARANCE: Appearance = { mode: 'light', fixed: false, from: '19:00', to: '07:00' }

const MODE_KEY = 'casevo_theme'
const AUTO_KEY = 'casevo_theme_auto'
const DARK_KEY = 'casevo_theme_dark'
const DAYS_AHEAD = 60
const MODES: ThemeMode[] = ['light', 'dark', 'device', 'auto']
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

// A stored / server value → a full, valid Appearance (anything odd → defaults).
export const normaliseAppearance = (raw: unknown): Appearance => {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return {
    mode: MODES.includes(r.mode as ThemeMode) ? (r.mode as ThemeMode) : DEFAULT_APPEARANCE.mode,
    fixed: r.fixed === true,
    from: typeof r.from === 'string' && HHMM.test(r.from) ? r.from : DEFAULT_APPEARANCE.from,
    to: typeof r.to === 'string' && HHMM.test(r.to) ? r.to : DEFAULT_APPEARANCE.to,
  }
}
const sameAppearance = (a: Appearance, b: Appearance): boolean => a.mode === b.mode && a.fixed === b.fixed && a.from === b.from && a.to === b.to

const storage = (): Storage | null => { try { return typeof window === 'undefined' ? null : window.localStorage } catch { return null } }

// This device's cached choice (default Light for anyone who never chose).
export const readAppearance = (): Appearance => {
  try {
    const s = storage()
    if (!s) return DEFAULT_APPEARANCE
    const auto = JSON.parse(s.getItem(AUTO_KEY) || '{}') as Record<string, unknown>
    return normaliseAppearance({ ...auto, mode: s.getItem(MODE_KEY) })
  } catch { return DEFAULT_APPEARANCE }
}
// Kept for callers that only need light / dark right now.
export const readTheme = (): Theme => resolveTheme(readAppearance(), new Date(), systemPrefersDark())

const toMinutes = (hhmm: string): number => +hhmm.slice(0, 2) * 60 + +hhmm.slice(3, 5)

// Automatic's dark periods [startMs, endMs] from the UK day before `now`
// for `days` days — sunset → next sunrise, or the fixed times.
export const darkPeriods = (a: Appearance, now: Date, days = DAYS_AHEAD): [number, number][] => {
  const today = ukParts(now)
  const out: [number, number][] = []
  for (let i = -1; i < days; i++) {
    const day = new Date(Date.UTC(today.y, today.m - 1, today.d + i))
    const next = new Date(Date.UTC(today.y, today.m - 1, today.d + i + 1))
    const [y, m, d] = [day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate()]
    const [y2, m2, d2] = [next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate()]
    if (a.fixed) {
      const from = toMinutes(a.from), to = toMinutes(a.to)
      if (from === to) continue // an empty window — never dark
      const start = ukInstant(y, m, d, from).getTime()
      const end = to > from ? ukInstant(y, m, d, to).getTime() : ukInstant(y2, m2, d2, to).getTime()
      out.push([start, end])
    } else {
      out.push([sunTimes(y, m, d).sunset.getTime(), sunTimes(y2, m2, d2).sunrise.getTime()])
    }
  }
  return out
}

export const systemPrefersDark = (): boolean => {
  try { return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches } catch { return false }
}

// Light or dark for a choice at an instant.
export const resolveTheme = (a: Appearance, now: Date, systemDark: boolean): Theme => {
  if (a.mode === 'dark' || a.mode === 'light') return a.mode
  if (a.mode === 'device') return systemDark ? 'dark' : 'light'
  const t = now.getTime()
  return darkPeriods(a, now, 2).some(([s, e]) => t >= s && t < e) ? 'dark' : 'light'
}

// Automatic: the next instant the theme flips (null for the other modes).
export const nextSwitch = (a: Appearance, now: Date): Date | null => {
  if (a.mode !== 'auto') return null
  const t = now.getTime()
  const edges = darkPeriods(a, now, 3).flat().filter((x) => x > t).sort((x, y) => x - y)
  return edges.length ? new Date(edges[0]) : null
}

// ── Applying ────────────────────────────────────────────────
// Toggle `dark` on <html>. `animate`: a soft cross-fade (index.css
// .theme-switching, which only transitions under no-preference reduced motion).
let fadeTimer: ReturnType<typeof setTimeout> | null = null
export const applyTheme = (theme: Theme, animate = false): void => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (root.classList.contains('dark') === (theme === 'dark')) return
  if (animate) {
    root.classList.add('theme-switching')
    if (fadeTimer) clearTimeout(fadeTimer)
    fadeTimer = setTimeout(() => { root.classList.remove('theme-switching'); fadeTimer = null }, 450)
  }
  root.classList.toggle('dark', theme === 'dark')
}

// Write this device's cache (mode, Automatic's settings and dark periods).
export const cacheAppearance = (a: Appearance, now = new Date()): void => {
  const s = storage()
  if (!s) return
  try {
    s.setItem(MODE_KEY, a.mode)
    s.setItem(AUTO_KEY, JSON.stringify({ fixed: a.fixed, from: a.from, to: a.to }))
    if (a.mode === 'auto') s.setItem(DARK_KEY, JSON.stringify(darkPeriods(a, now)))
    else s.removeItem(DARK_KEY)
  } catch { /* private mode — the in-DOM apply still happens */ }
}

// ── The live store + controller ─────────────────────────────
interface AppearanceState { appearance: Appearance; theme: Theme }
export const useAppearance = create<AppearanceState>(() => ({ appearance: readAppearance(), theme: readTheme() }))

// Re-resolve now and apply (animated when it changes live).
const refresh = (animate: boolean): void => {
  const { appearance } = useAppearance.getState()
  const theme = resolveTheme(appearance, new Date(), systemPrefersDark())
  applyTheme(theme, animate)
  if (theme !== useAppearance.getState().theme) useAppearance.setState({ theme })
}

let switchTimer: ReturnType<typeof setTimeout> | null = null
// setTimeout drifts or stalls while a laptop sleeps, so a long wait is
// broken into ≤ 30-minute steps; tab focus / wake re-checks at once too.
const MAX_WAIT = 30 * 60 * 1000
const schedule = (): void => {
  if (switchTimer) { clearTimeout(switchTimer); switchTimer = null }
  const next = nextSwitch(useAppearance.getState().appearance, new Date())
  if (!next) return
  const wait = Math.max(0, Math.min(next.getTime() - Date.now(), MAX_WAIT))
  switchTimer = setTimeout(() => {
    switchTimer = null
    // Keep the boot cache's dark periods running ahead.
    const a = useAppearance.getState().appearance
    if (a.mode === 'auto') cacheAppearance(a)
    refresh(true)
    schedule()
  }, wait + 500)
}

// Change the choice on this device: cache, apply (soft), reschedule. The
// caller saves it to the account (Settings → updateMeApi).
export const setAppearance = (a: Appearance): void => {
  const next = normaliseAppearance(a)
  cacheAppearance(next)
  useAppearance.setState({ appearance: next })
  refresh(true)
  schedule()
}
// The account's saved choice (GET /api/auth/me): adopt it here when it
// differs from the device cache. null = never saved on the account.
export const adoptAccountAppearance = (raw: unknown): void => {
  if (raw === null || raw === undefined) return
  const a = normaliseAppearance(raw)
  if (sameAppearance(a, useAppearance.getState().appearance)) return
  setAppearance(a)
}

// Start once (App): apply the cached choice, schedule Automatic's next
// switch, follow the device setting live, and re-check on focus / wake.
export const startThemeController = (): (() => void) => {
  refresh(false)
  schedule()
  const recheck = (): void => { if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { refresh(true); schedule() } }
  let mq: MediaQueryList | null = null
  const onScheme = (): void => { if (useAppearance.getState().appearance.mode === 'device') refresh(true) }
  try { mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null } catch { mq = null }
  mq?.addEventListener?.('change', onScheme)
  window.addEventListener('focus', recheck)
  window.addEventListener('pageshow', recheck)
  document.addEventListener('visibilitychange', recheck)
  return () => {
    if (switchTimer) { clearTimeout(switchTimer); switchTimer = null }
    mq?.removeEventListener?.('change', onScheme)
    window.removeEventListener('focus', recheck)
    window.removeEventListener('pageshow', recheck)
    document.removeEventListener('visibilitychange', recheck)
  }
}
